import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatNGN } from "@/utils/moneyFormatter";
import { parseMonthLocal } from "@/pages/dashboard/contributionHistory.utils";

const BAR_FILL = "#C4B5FD";

export interface MonthlyContributionChartRow {
  month: string;
  totalContribution: number | null;
  employeeCount: number | null;
  hasContribution: boolean;
}

type ChartPoint = {
  monthKey: string;
  monthLabel: string;
  amount: number;
  employeeCount: number | null;
};

function formatYAxisTick(value: number): string {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return Number.isInteger(m) ? `${m}M` : `${m.toFixed(1)}M`;
  }
  if (value >= 1_000) {
    const k = value / 1_000;
    return Number.isInteger(k) ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return `${value}`;
}

function ContributionTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: unknown }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as ChartPoint | undefined;
  if (!row) return null;

  return (
    <div className="rounded-lg border border-stroke bg-white px-3 py-2 shadow-[0_4px_20px_rgba(0,0,0,0.1)]">
      <p className="font-clash-display text-xs text-medium-grey">
        {row.monthLabel}
      </p>
      <p className="mt-1 font-clash-display text-sm font-semibold text-black-dark-grey">
        {row.amount > 0 ? formatNGN(row.amount) : "—"}
      </p>
      {typeof row.employeeCount === "number" && row.amount > 0 && (
        <p className="mt-1 font-clash-display text-xs text-medium-dark-grey">
          ({row.employeeCount} employees covered)
        </p>
      )}
    </div>
  );
}

export function MonthlyContributionChart({
  data,
}: {
  data: MonthlyContributionChartRow[];
}) {
  const chartPoints = useMemo<ChartPoint[]>(() => {
    return data.map((row) => {
      const d = parseMonthLocal(row.month);
      return {
        monthKey: d.toLocaleDateString("en-US", { month: "short" }),
        monthLabel: d.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        }),
        amount:
          typeof row.totalContribution === "number" ? row.totalContribution : 0,
        employeeCount: row.employeeCount,
      };
    });
  }, [data]);

  const maxAmount = useMemo(
    () => Math.max(0, ...chartPoints.map((p) => p.amount)),
    [chartPoints]
  );

  const domainMax = useMemo(() => {
    if (maxAmount <= 0) return 1_000_000;
    return maxAmount * 1.1;
  }, [maxAmount]);

  return (
    <div className="rounded-lg border border-stroke bg-white p-4 shadow-sm">
      <h3 className="font-clash-display text-base font-semibold text-black-dark-grey">
        Monthly Contribution Chart
      </h3>
      <div className="mt-4 h-[320px] w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartPoints}
            margin={{ top: 8, right: 8, left: 4, bottom: 8 }}
            barCategoryGap="18%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#E5E7EB"
            />
            <XAxis
              dataKey="monthKey"
              tick={{ fontSize: 11, fill: "#6B7280" }}
              interval={0}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatYAxisTick}
              width={44}
              tick={{ fontSize: 11, fill: "#6B7280" }}
              axisLine={false}
              tickLine={false}
              domain={[0, domainMax]}
            />
            <Tooltip
              content={({ active, payload }) => (
                <ContributionTooltip active={active} payload={payload} />
              )}
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
            />
            <Bar
              dataKey="amount"
              fill={BAR_FILL}
              radius={[4, 4, 0, 0]}
              maxBarSize={48}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
